// Proximity to chapters and per-frame stop updates.
import * as THREE from 'three';
import { cam } from '../core/scene.js';
import { V } from '../world/sagrada.js';
import { S, wrapAngle } from './ride.js';
import { TOUCH } from './input.js';
import { STOPS } from '../data/stops.js';
import { loc, tr } from '../ui/lang.js';
import { arrow, nextStop, stopPrompt, visited } from './stop-markers.js';
import { openStop, unlocked } from './stop-panel.js';
import { finaleSeen, startFinale } from './finale.js';
import { state } from '../core/state.js';
import { panel } from '../dom.js';
import { streets } from '../world/landmarks.js';
import { guideHeading, routeMetres, streetRoute } from '../rules/route.js';

export let toggleStop, updateStops;

export function buildStops() {
  const gpsDist = document.createElement('div');
  gpsDist.id = 'gpsDist';
  document.body.appendChild(gpsDist);
  const gpsPos = { x: 0, y: 0, ok: false };
  toggleStop = function toggleStop(onlyClose) {
    if (state.panelOpen) {
      state.panelOpen = false;
      panel.classList.remove('open');
      if (visited.size === STOPS.length && !finaleSeen) startFinale();
      return;
    }
    if (!onlyClose && state.nearStop >= 0 && unlocked(state.nearStop)) openStop(state.nearStop);
  };
  updateStops = function updateStops(time) {
    state.nearStop = -1;
    STOPS.forEach((st, i) => {
      const d = Math.hypot(S.x - st.x, S.z - st.z),
        s = 1 + Math.sin(time * 3 + i) * 0.06;
      st.ring.scale.set(s, 1, s);
      // the light beam fades out as you arrive, so it never blocks the view
      st.beam.material.opacity = THREE.MathUtils.clamp((d - 3) / 10, 0.08, 1);
      const p = V(st.x, st.g.position.y + 6.2 + Math.sin(time * 1.5 + i) * 0.15, st.z).project(cam);
      // only nearby stops and the next one are labelled, so labels don't clutter the view through buildings
      const show =
        p.z < 1 &&
        state.mode !== 'intro' &&
        state.mode !== 'finale' &&
        Math.abs(p.x) < 1.1 &&
        Math.abs(p.y) < 1.1 &&
        (d < 40 || i === nextStop() || state.mode === 'overview');
      st.label.style.display = show ? 'flex' : 'none';
      if (show) {
        st.label.style.left = ((p.x + 1) / 2) * innerWidth + 'px';
        st.label.style.top = ((1 - p.y) / 2) * innerHeight + 'px';
        st.label.style.opacity = d < 4 ? 0.35 : 1;
      }
      if (d < 3.4 && state.mode === 'ride') state.nearStop = i;
    });
    if (state.mode === 'ride' && !state.panelOpen && state.nearStop >= 0) {
      // chapters open in order: a stop ahead of the story only says which one comes first
      stopPrompt.classList.toggle('locked', !unlocked(state.nearStop));
      stopPrompt.innerHTML = unlocked(state.nearStop)
        ? `<kbd>${TOUCH ? '👆' : 'E'}</kbd>${STOPS[state.nearStop].place} · ${loc(STOPS[state.nearStop]).k}`
        : `<kbd>🔒</kbd>${tr('lockedFirst')} ${nextStop() + 1} · ${STOPS[nextStop()].place}`;
      stopPrompt.style.display = 'block';
    } else stopPrompt.style.display = 'none';
    const n = nextStop();
    arrow.visible = state.mode === 'ride' && n >= 0 && !state.panelOpen;
    const wasShown = gpsPos.ok;
    state.route = null;
    gpsPos.ok = false;
    if (arrow.visible) {
      arrow.position.set(S.x, S.y + 3.3 + Math.sin(time * 3) * 0.12, S.z);
      // GPS: follow the streets to the next stop, recalculated every frame so a missed turn reroutes itself
      const route = streetRoute([S.x, S.z], [STOPS[n].x, STOPS[n].z], streets),
        yaw = guideHeading(route.pts, 8);
      state.route = route;
      arrow.rotation.y += wrapAngle(yaw - arrow.rotation.y) * 0.12;
      const p = V(S.x, S.y + 2.3, S.z).project(cam);
      if (state.nearStop < 0 && p.z < 1) {
        gpsDist.textContent = routeMetres(route.len) + ' m';
        // heavily smoothed, so camera shake at speed doesn't make the label jitter
        const tx = ((p.x + 1) / 2) * innerWidth,
          ty = ((1 - p.y) / 2) * innerHeight;
        if (!wasShown) Object.assign(gpsPos, { x: tx, y: ty });
        gpsPos.ok = true;
        gpsPos.x += (tx - gpsPos.x) * 0.06;
        gpsPos.y += (ty - gpsPos.y) * 0.06;
        gpsDist.style.left = Math.round(gpsPos.x) + 'px';
        gpsDist.style.top = Math.round(gpsPos.y) + 'px';
        gpsDist.style.display = 'block';
      }
      arrow.rotation.z = Math.sin(time * 2) * 0.06; // a gentle rock
      arrow.userData.mat.color.set(STOPS[n].color).lerp(new THREE.Color(0xffffff), 0.15);
      arrow.userData.mat.emissive.set(STOPS[n].color);
    }
    if (!gpsPos.ok) gpsDist.style.display = 'none';
  };
}
